import { useT } from '@kmgestao/i18n';
import { Label, PageSectionCard, Select, Skeleton, toast } from '@kmgestao/ui';

import {
  filterAvailable,
  resolveOrphanState,
  useAssignModuleFallback,
  useAssignModuleModel,
  useModels,
  useModuleConfigs,
  type ModelOption,
  type ModuleConfigRow,
} from '../../../../features/ai-admin/index.js';

/** Sentinel para "sem fallback" — `<select>` só trabalha com strings, não
 * com null. O valor precisa ser algo que nunca aparecerá como UUID real. */
const NO_FALLBACK_VALUE = '__no_fallback__';

export function ModuleAssignments() {
  const { t } = useT('ai-admin');
  const configs = useModuleConfigs();
  const models = useModels();

  return (
    <PageSectionCard title={t('modules.title')} description={t('modules.description')}>
      {configs.isLoading || models.isLoading ? (
        <ul className="space-y-3">
          {[0, 1, 2].map((i) => (
            <li key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_16rem]">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-10 w-full" />
            </li>
          ))}
        </ul>
      ) : configs.isError ? (
        <p className="text-sm text-destructive">
          {configs.error instanceof Error ? configs.error.message : 'Erro ao carregar.'}
        </p>
      ) : !configs.data || configs.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('modules.empty')}</p>
      ) : (
        <ul className="space-y-3">
          {configs.data.map((c) => (
            <ModuleRow key={c.id} config={c} models={models.data ?? []} />
          ))}
        </ul>
      )}
    </PageSectionCard>
  );
}

function ModuleRow({
  config,
  models,
}: {
  config: ModuleConfigRow;
  models: readonly ModelOption[];
}) {
  const { t } = useT('ai-admin');
  const assignDefault = useAssignModuleModel();
  const assignFallback = useAssignModuleFallback();

  const labelKey = `modules.keys.${config.moduleKey}` as const;
  const translatedLabel = t(labelKey, { defaultValue: config.namePtBr });

  // `models` vem com modelos indisponíveis também (flag isAvailable).
  // Dividimos em dois pra: (a) popular só os disponíveis no dropdown,
  // (b) detectar se `defaultModelId`/`fallbackModelId` é órfão. Ver
  // orphan-state.ts pro contexto completo do bug 2026-04-24.
  const availableModels = filterAvailable(models);
  const defaultOrphan = resolveOrphanState(config.defaultModelId, models);
  const fallbackOrphan = resolveOrphanState(config.fallbackModelId, models);
  const isDefaultOrphan = defaultOrphan.kind !== 'ok';
  const isFallbackOrphan = fallbackOrphan.kind !== 'ok';

  const handleDefaultChange = async (modelId: string) => {
    if (!modelId) return;
    if (isDefaultOrphan && modelId === config.defaultModelId) return;
    try {
      await assignDefault.mutateAsync({ moduleKey: config.moduleKey, modelId });
      toast.success(t('modules.success'));
    } catch (err) {
      toast.error(t('modules.error'), {
        description: err instanceof Error ? err.message : '',
      });
    }
  };

  const handleFallbackChange = async (rawValue: string) => {
    // `NO_FALLBACK_VALUE` é o sentinel do <option> "— sem fallback"; mapeia
    // pra null na RPC que limpa a coluna.
    const modelId = rawValue === NO_FALLBACK_VALUE ? null : rawValue;
    if (isFallbackOrphan && modelId === config.fallbackModelId) return;
    try {
      await assignFallback.mutateAsync({ moduleKey: config.moduleKey, modelId });
      toast.success(t('modules.success'));
    } catch (err) {
      toast.error(t('modules.error'), {
        description: err instanceof Error ? err.message : '',
      });
    }
  };

  // Valor atual do select de fallback:
  //   - null (sem fallback cadastrado) → NO_FALLBACK_VALUE ("— sem fallback")
  //   - UUID válido → o próprio UUID (disponível OU órfão)
  const fallbackSelectValue = config.fallbackModelId ?? NO_FALLBACK_VALUE;

  return (
    <li className="rounded-xl border border-border/70 bg-muted/20 p-4">
      <div className="space-y-3">
        <div>
          <Label htmlFor={`module-${config.moduleKey}-default`}>{translatedLabel}</Label>
          <p className="text-sm text-muted-foreground">{config.namePtBr}</p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {/* === Default === */}
          <div className="space-y-1">
            <Label
              htmlFor={`module-${config.moduleKey}-default`}
              className="text-xs text-muted-foreground"
            >
              {t('modules.defaultLabel', { defaultValue: 'Default' })}
            </Label>
            <Select
              id={`module-${config.moduleKey}-default`}
              value={config.defaultModelId ?? ''}
              onChange={(e) => handleDefaultChange(e.target.value)}
              disabled={assignDefault.isPending}
              aria-invalid={isDefaultOrphan || undefined}
            >
              <option value="" disabled>
                {t('modules.placeholder')}
              </option>
              {isDefaultOrphan && config.defaultModelId ? (
                <option value={config.defaultModelId}>{defaultOrphan.label}</option>
              ) : null}
              {availableModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.providerName} · {m.modelId}
                </option>
              ))}
            </Select>
            {isDefaultOrphan ? (
              <p className="text-xs text-destructive" role="alert">
                {t('modules.orphanWarning', {
                  defaultValue: 'Configuração atual inválida — escolha um modelo disponível.',
                })}
              </p>
            ) : null}
          </div>

          {/* === Fallback === */}
          <div className="space-y-1">
            <Label
              htmlFor={`module-${config.moduleKey}-fallback`}
              className="text-xs text-muted-foreground"
            >
              {t('modules.fallbackLabel', {
                defaultValue: 'Fallback (se o default falhar)',
              })}
            </Label>
            <Select
              id={`module-${config.moduleKey}-fallback`}
              value={fallbackSelectValue}
              onChange={(e) => handleFallbackChange(e.target.value)}
              disabled={assignFallback.isPending}
              aria-invalid={isFallbackOrphan || undefined}
            >
              <option value={NO_FALLBACK_VALUE}>
                {t('modules.noFallback', { defaultValue: '— sem fallback' })}
              </option>
              {isFallbackOrphan && config.fallbackModelId ? (
                <option value={config.fallbackModelId}>{fallbackOrphan.label}</option>
              ) : null}
              {availableModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.providerName} · {m.modelId}
                </option>
              ))}
            </Select>
            {isFallbackOrphan ? (
              <p className="text-xs text-destructive" role="alert">
                {t('modules.orphanWarning', {
                  defaultValue: 'Configuração atual inválida — escolha um modelo disponível.',
                })}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
}
